import DeleteIcon from "@material-ui/icons/Delete";
import { useContext, useEffect, useMemo, useState } from "react";

import { Box, IconButton, Typography } from "@mui/material";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import RegularButton from "components/CustomButtons/Button";
import CustomInput from "components/CustomInput/CustomInput";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import { LoginContext } from "contexts/LoginContext";
import ProgramService from "services/ProgramServices/ProgramService";


const createEmptySlab = () => ({
    slabId: null,
    name: "",
    amount: "",
    durationInMonths: "",
});


const ProgramForm = ({ program, onSubmit, onClose }) => {
    const { providerId, user } = useContext(LoginContext);
    const [data, setData] = useState({
        name: "",
        durationInMonths: "",
        description: "",
        baseFee: "",
        slabs: [],
    });

    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const totalSlabAmount = useMemo(() => {
        return data.slabs.reduce((sum, s) => sum + Number(s.amount || 0), 0);
    }, [data.slabs]);

    const pendingAmount = Number(data.baseFee || 0) - totalSlabAmount;

    useEffect(() => {
        if (!program) return;

        setData({
            name: program.name ?? "",
            description: program.description ?? "",
            durationInMonths: program.durationInMonths ?? "",
            baseFee: program.baseFee ?? "",
            slabs: Array.isArray(program.slabs)
                ? program.slabs.map((s) => ({
                    slabId: s.slabId ?? null,
                    name: s.name ?? "",
                    amount: s.amount ?? "",
                    durationInMonths: s.durationInMonths ?? "",
                }))
                : [],
        });
    }, [program]);

    const handleChange = (key, value) => {
        setData({ ...data, [key]: value });
    };

    const addSlab = () => {
        setData({ ...data, slabs: [...data.slabs, createEmptySlab()] });
    };

    const updateSlab = (index, key, value) => {
        const slabs = data.slabs.map((slab, i) =>
            i === index ? { ...slab, [key]: value } : slab
        );
        setData({ ...data, slabs });
    };

    const removeSlab = (index) => {
        setData({ ...data, slabs: data.slabs.filter((_, i) => i !== index) });
    };

    const validate = () => {
        const err = {};
        if (!data.name) err.name = "Program name required";
        if (!data.durationInMonths) err.durationInMonths = "Duration required";
        if (!data.baseFee) err.baseFee = "Program price required";
        if (data.slabs.length > 0 && totalSlabAmount !== Number(data.baseFee)) {
            err.slabs = "Slab amounts do not match program fee.";
        }
        setErrors(err);
        return Object.keys(err).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;

        try {
            setSaving(true);

            const payload = {
                providerId,
                code: data.name?.toUpperCase().replace(/\s+/g, "-"),
                name: data.name,
                description: data.description,
                durationInMonths: Number(data.durationInMonths),
                baseFee: Number(data.baseFee),
                updatedBy: user?.name || "system",
                createdBy: user?.name || "system",

                slabs: data.slabs.map((s, index) => ({
                    slabId: s.slabId || null,
                    slabName: s.name,
                    amount: Number(s.amount),
                    durationInMonths: Number(s.durationInMonths),
                    sequenceOrder: index + 1
                }))
            };

            if (program?.programId) {
                try {
                    await ProgramService.updateProgramWithSlabs(
                        program.programId,
                        payload
                    );
                } catch (error) {
                    console.log("Program updation failed");
                }

            } else {
                try {
                    const res = await ProgramService.createProgram(payload);
                    const programId = res?.programId;
                    if (!programId) console.log("Program creation failed");
                    if (data?.slabs?.length > 0) {
                        const addPayload = data?.slabs?.map((s, index) => ({
                            slabName: s.name,
                            amount: Number(s.amount),
                            durationInMonths: Number(s.durationInMonths),
                            sequenceOrder: index + 1,
                        }));
                        await ProgramService.addSlab(programId, addPayload);
                    }
                } catch (error) {
                    console.log("Program creation failed");
                }
            }

            onClose();
        } catch (error) {
            console.error("Error saving program", error);
            alert("Failed to save program");
        } finally {
            setSaving(false);
        }
    };


    return (
        <Card>
            <CardHeader color="rose" icon>
                <CardIcon color="rose">
                    <i className="material-icons">add</i>
                </CardIcon>
                <h4>{program ? "Edit Program" : "Add Program"}</h4>
            </CardHeader>

            <CardBody>
                <GridContainer>
                    <GridItem xs={12} sm={6}>
                        <CustomInput
                            labelText="Program Name *"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                                value: data.name,
                                onChange: (e) => handleChange("name", e.target.value),
                            }}
                            error={!!errors.name}
                            helperText={errors.name}
                        />
                    </GridItem>

                    <GridItem xs={12} sm={3}>
                        <CustomInput
                            labelText="Duration (Months) *"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                                type: "number",
                                value: data.durationInMonths,
                                onChange: (e) =>
                                    handleChange("durationInMonths", e.target.value),
                            }}
                            error={!!errors.durationInMonths}
                            helperText={errors.durationInMonths}
                        />
                    </GridItem>

                    <GridItem xs={12} sm={3}>
                        <CustomInput
                            labelText="Program Price *"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                                type: "number",
                                value: data.baseFee,
                                onChange: (e) => handleChange("baseFee", e.target.value),
                            }}
                            error={!!errors.baseFee}
                            helperText={errors.baseFee}
                        />
                    </GridItem>

                    <GridItem xs={12} sm={12}>
                        <CustomInput
                            labelText="Program Description (Optional)"
                            formControlProps={{ fullWidth: true }}
                            inputProps={{
                                multiline: true,
                                minRows: 1,
                                value: data.description,
                                onChange: (e) =>
                                    handleChange("description", e.target.value),
                            }}
                        />
                    </GridItem>
                </GridContainer>

                <Box mt={3}>
                    <Typography sx={{ textTransform: 'capitalize' }} variant="subtitle1" mb={2}>
                        Payment Slabs (Optional)
                    </Typography>

                    {data.slabs.map((slab, i) => (
                        <GridContainer spacing={2} key={slab.slabId ?? `new-${i}`} alignItems="center">
                            <GridItem xs={12} sm={4}>
                                <CustomInput
                                    labelText="Slab Name"
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                        value: slab.name,
                                        onChange: (e) =>
                                            updateSlab(i, "name", e.target.value),
                                    }}
                                />
                            </GridItem>

                            <GridItem xs={12} sm={3}>
                                <CustomInput
                                    labelText="Amount"
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                        type: "number",
                                        value: slab.amount,
                                        onChange: (e) =>
                                            updateSlab(i, "amount", e.target.value),
                                    }}
                                />
                            </GridItem>

                            <GridItem xs={12} sm={4}>
                                <CustomInput
                                    labelText="Duration (After Months)"
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                        type: "number",
                                        value: slab.durationInMonths,
                                        onChange: (e) =>
                                            updateSlab(i, "durationInMonths", e.target.value),
                                    }}
                                />
                            </GridItem>

                            <GridItem xs={12} sm={1}>
                                <IconButton onClick={() => removeSlab(i)}>
                                    <DeleteIcon />
                                </IconButton>
                            </GridItem>
                        </GridContainer>
                    ))}

                    <RegularButton
                        color="primary"
                        onClick={addSlab}
                        style={{ marginTop: 10, textTransform: "none" }}
                    >
                        + Add Slab
                    </RegularButton>

                    {data.slabs.length > 0 && (
                        <Box mt={2}>
                            {pendingAmount !== 0 && (
                                <Typography variant="body2" color="error">
                                    Pending Amount: ₹&nbsp;{pendingAmount}
                                </Typography>
                            )}
                            {errors.slabs && (
                                <Typography color="error">{errors.slabs}</Typography>
                            )}
                        </Box>
                    )}
                </Box>

                <Box mt={3} textAlign="right">
                    <RegularButton
                        color="rose"
                        onClick={handleSave}
                        disabled={saving}
                        style={{ textTransform: "none" }}
                    >
                        {saving ? "Saving..." : "Save Program"}
                    </RegularButton>
                </Box>
            </CardBody>
        </Card>
    );
};

export default ProgramForm;
